/*
 * CHAT CONTEXT - GLOBAL RIGHT PANEL STATE MANAGEMENT
 * 
 * Provides global state management for the right side panel.
 * 
 * Key Features:
 * - Manages panel state (closed, right-panel)
 * - Content switching: Chat or Editor (no visible tabs - contextual display)
 * - Animation state management for smooth transitions
 * - Helper functions for panel operations
 * - State change callbacks for responsive grid adjustments
 * 
 * Two-State Panel System:
 * 1. 'closed': Panel is not visible
 * 2. 'right-panel': Panel is open on the right side (2/3 main content, 1/3 panel)
 * 
 * Content System (no visible tabs):
 * - 'chat': Default, triggered by navbar chat button
 * - 'editor': Dynamic content, triggered by specific actions (e.g., Kanban gear icon)
 * - Each content type provides its own header - no shared tab bar
 * 
 * Animation States:
 * - 'entering': Panel is animating in
 * - 'entered': Panel is fully visible and stable
 * - 'exiting': Panel is animating out
 * - 'exited': Panel is fully hidden and stable
 * 
 * Integration:
 * - Used by App.tsx for main panel management
 * - Used by grid components (Dashboard, AccountList, ContactList, Notes) for responsive behavior
 * - Automatically saves state to localStorage for persistence across sessions
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

// Chat state types
export type ChatState = 'closed' | 'right-panel';
export type ChatAnimationState = 'entering' | 'entered' | 'exiting' | 'exited';

// Tab types for right panel
export type TabType = 'chat' | 'editor';
export type EditorContentType = 'kanban' | 'settings'; // extensible for future editors

// Editor content structure
export interface EditorContent {
  type: EditorContentType;
  props?: Record<string, unknown>;
}

// Context interface
interface ChatContextType {
  // Panel State
  chatState: ChatState;
  chatAnimationState: ChatAnimationState;

  // Tab State
  activeTab: TabType;
  editorContent: EditorContent | null;

  // State setters
  setChatState: (state: ChatState) => void;
  setChatAnimationState: (state: ChatAnimationState) => void;
  setActiveTab: (tab: TabType) => void;

  // Helper functions
  openChat: () => void;
  closeChat: () => void;
  openEditor: (content: EditorContent) => void;
  updateEditorContent: (content: EditorContent) => void;

  // Computed properties for responsive components
  isChatVisible: boolean;
}

// Create context
const ChatContext = createContext<ChatContextType | undefined>(undefined);

// Provider props
interface ChatProviderProps {
  children: ReactNode;
}

// Provider component
export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  // Initialize chat state from localStorage
  const [chatState, setChatStateInternal] = useState<ChatState>(() => {
    const saved = localStorage.getItem('chatState');
    if (saved === 'right-panel') {
      return 'right-panel';
    }
    if (saved === 'closed') {
      return 'closed';
    }
    // Gracefully degrade legacy 'expanded' state to right panel
    if (saved === 'expanded') {
      return 'right-panel';
    }
    return 'closed';
  });

  // Initialize animation state based on chat state
  const [chatAnimationState, setChatAnimationStateInternal] = useState<ChatAnimationState>(() => {
    // If chat is open on load, set to 'entered', otherwise 'exited'
    const saved = localStorage.getItem('chatState');
    return (saved && saved !== 'closed') ? 'entered' : 'exited';
  });

  // Tab state - defaults to 'chat'
  const [activeTab, setActiveTabInternal] = useState<TabType>('chat');

  // Editor content - null when no editor is active
  const [editorContent, setEditorContent] = useState<EditorContent | null>(null);

  // Update localStorage when chat state changes - only persist if showing Chat (not Editor)
  useEffect(() => {
    // Only persist panel state when Chat is active; Editor content is transient
    if (activeTab === 'chat') {
      localStorage.setItem('chatState', chatState);
    } else {
      // Don't persist editor panel state - close on reload
      localStorage.setItem('chatState', 'closed');
    }
  }, [chatState, activeTab]);

  // Wrapped setState functions to handle persistence
  const setChatState = (state: ChatState) => {
    setChatStateInternal(state);
  };

  const setChatAnimationState = (state: ChatAnimationState) => {
    setChatAnimationStateInternal(state);
  };

  // Tab switching function
  const setActiveTab = (tab: TabType) => {
    setActiveTabInternal(tab);
  };

  // Helper function: Open chat (transition from closed to right-panel, sets chat tab)
  const openChat = () => {
    setActiveTabInternal('chat');
    if (chatState === 'closed') {
      setChatState('right-panel');
      setChatAnimationState('entering');
      setTimeout(() => setChatAnimationState('entered'), 250);
    }
    // If already visible, just switch to chat tab
  };

  // Helper function: Close panel (transition to closed)
  const closeChat = () => {
    setChatAnimationState('exiting');
    // Start the exit animation, then set state to closed after animation completes
    setTimeout(() => {
      setChatState('closed');
      setChatAnimationState('exited');
    }, 250); // Match CSS animation duration
  };

  // Helper function: Open editor (opens panel with editor tab and sets content)
  const openEditor = useCallback((content: EditorContent) => {
    setEditorContent(content);
    setActiveTabInternal('editor');
    if (chatState === 'closed') {
      setChatState('right-panel');
      setChatAnimationState('entering');
      setTimeout(() => setChatAnimationState('entered'), 250);
    }
    // If already visible, just switch to editor tab
  }, [chatState]);

  // Helper function: Update editor content without animation (for reactive updates)
  const updateEditorContent = useCallback((content: EditorContent) => {
    setEditorContent(content);
  }, []);

  // Computed properties for responsive components
  const isChatVisible = chatState !== 'closed' || chatAnimationState === 'exiting';

  // Context value
  const contextValue: ChatContextType = {
    // Panel State
    chatState,
    chatAnimationState,

    // Tab State
    activeTab,
    editorContent,

    // State setters
    setChatState,
    setChatAnimationState,
    setActiveTab,

    // Helper functions
    openChat,
    closeChat,
    openEditor,
    updateEditorContent,

    // Computed properties
    isChatVisible
  };

  return (
    <ChatContext.Provider value={contextValue}>
      {children}
    </ChatContext.Provider>
  );
};

// Custom hook to use chat context
export const useChatContext = (): ChatContextType => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
};

export default ChatContext;
